import { useOrganization } from '@clerk/react';
import type { OrganizationMembershipResource } from '@clerk/shared/types';
import { useMemo } from 'react';
import { useMemberships } from '../hooks/useMemberships';

export type PeopleRow = {
  id: string;
  membershipId?: number;
  type: 'member' | 'invitation';
  name: string;
  email: string;
  role: string;
  imageUrl?: string;
  subsidiaryId?: number | null;
  remove?: () => void;
};

// Builds out the rows for the people grid - most of the data comes straight from the clerk hooks,
// but we also need to pull in a little bit of data from our own Membership model
export default function usePeopleRows(): PeopleRow[] {
  const {
    memberships: clerkMemberships,
    invitations: clerkInvitations,
    organization: clerkOrg,
  } = useOrganization({ memberships: { pageSize: 500 }, invitations: { pageSize: 500 } });

  const clerkUsers = clerkMemberships?.data;
  const clerkPending = clerkInvitations?.data;

  const clerkIds = useMemo(() => clerkUsers?.map((member) => member.id) ?? [], [clerkUsers]);
  const { memberships } = useMemberships(clerkIds);

  return useMemo<PeopleRow[]>(() => {
    const activeMembers: PeopleRow[] =
      clerkUsers?.map((member: OrganizationMembershipResource) => {
        const membership = memberships?.find((m: { clerkId: string }) => m.clerkId === member.id);
        return {
          id: member.publicUserData?.userId ?? '',
          membershipId: membership?.id,
          type: 'member' as const,
          name: [member.publicUserData?.firstName, member.publicUserData?.lastName].filter(Boolean).join(' ') || '--',
          email: member.publicUserData?.identifier ?? '',
          role: member.role,
          imageUrl: member.publicUserData?.imageUrl,
          subsidiaryId: membership?.subsidiaryId,
          remove: () => clerkOrg?.removeMember(member.publicUserData?.userId ?? ''),
        };
      }) ?? [];

    const pendingMembers: PeopleRow[] =
      clerkPending?.map((invitation) => ({
        id: invitation.id,
        type: 'invitation',
        name: invitation.status.slice(0, 1).toUpperCase() + invitation.status.slice(1),
        email: invitation.emailAddress,
        role: invitation.role,
        remove: () => invitation.revoke(),
      })) ?? [];

    return [...activeMembers, ...pendingMembers];
  }, [clerkUsers, clerkPending, memberships, clerkOrg?.removeMember, clerkOrg]);
}
